import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ShoppingCart } from 'lucide-react';
import { Button } from '@edusense/shared-ui-kit';
import { useEnrollCourse, EnrollmentCreateRequestEnrollmentType, getGetCourseDetailQueryKey } from '@edusense/course-client';
import { useAuth } from '@edusense/shared-auth-core';
import { isLearnSurface } from '@edusense/shared-constants';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useCancelEnrollment } from '@edusense/course-client';
const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;

    button {
      width: 100%;
    }
  }
`;
const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;
const CancelButton = styled(Button)`
  white-space: nowrap;
  min-width: fit-content;
`;
const EnrollButton = styled(Button)`
  white-space: nowrap;
  min-width: fit-content;
`;
export const CourseHeaderActions = ({ courseId, courseTitle, isEnrolled, isFree, showCancelButton, onEnrollmentSuccess, onCancelEnrollment, currentEnrollmentId, isInstructor, onEditClick })=>{
    const [isEnrolling, setIsEnrolling] = useState(false);
    const [enrollError, setEnrollError] = useState(null);
    const [localEnrolled, setLocalEnrolled] = useState(Boolean(isEnrolled));
    const [optimisticState, setOptimisticState] = useState('none');
    const { isAuthenticated, user } = useAuth();
    const queryClient = useQueryClient();
    // 수강 취소 mutation
    const cancelMutation = useCancelEnrollment({
        mutation: {
            onSuccess: ()=>{
                console.log('[Course Enrollment] Cancellation successful');
                toast.success('수강이 취소되었습니다.');
                setLocalEnrolled(false);
                setOptimisticState('cancel');
            },
            onError: (error)=>{
                console.error('[Course Enrollment] Cancel failed:', error);
                toast.error('수강 취소 중 오류가 발생했습니다.');
                setOptimisticState('none');
                setLocalEnrolled(true);
            }
        }
    });
    // ✅ 이미 수강 중인 경우 버튼 클릭을 완전히 막기
    const isAlreadyEnrolled = localEnrolled || Boolean(isEnrolled);
    // 디버깅 로그
    console.log('[CourseHeaderActions Debug]', {
        courseId,
        isEnrolled,
        localEnrolled,
        optimisticState,
        showCancelButton,
        resolvedIsEnrolled: Boolean(localEnrolled),
        resolvedShowCancelButton: Boolean(showCancelButton || localEnrolled && onCancelEnrollment),
        isFree,
        isAuthenticated,
        userId: user?.id
    });
    useEffect(()=>{
        console.log('[CourseHeaderActions] useEffect triggered', {
            optimisticState,
            isEnrolled,
            localEnrolled
        });
        if (optimisticState === 'enroll') {
            console.log('[CourseHeaderActions] Optimistic state: enroll');
            if (isEnrolled) {
                console.log('[CourseHeaderActions] isEnrolled is true, setting localEnrolled to true');
                setLocalEnrolled(true);
                setOptimisticState('none');
            } else {
                console.log('[CourseHeaderActions] isEnrolled is still false, keeping optimistic state');
                // API 성공 후 일정 시간 후에 강제 업데이트 시도
                setTimeout(()=>{
                    if (optimisticState === 'enroll' && !isEnrolled) {
                        console.log('[CourseHeaderActions] Forcing localEnrolled to true after timeout');
                        setLocalEnrolled(true);
                        setOptimisticState('none');
                    }
                }, 1000);
            }
            return;
        }
        if (optimisticState === 'cancel') {
            // 수강취소의 경우, 서버 응답(isEnrolled prop)에 따라 상태 결정
            // isEnrolled가 false가 되면 그때 UI 변경
            if (isEnrolled === false) {
                setLocalEnrolled(false);
                setOptimisticState('none');
            } else {
                // 서버에서 아직 응답이 없거나 취소가 실패한 경우 현재 상태 유지
                setLocalEnrolled(true);
            }
            return;
        }
        console.log('[CourseHeaderActions] Setting localEnrolled based on isEnrolled prop');
        setLocalEnrolled(Boolean(isEnrolled));
    }, [
        isEnrolled,
        optimisticState
    ]);
    // 수강신청 mutation
    const enrollMutation = useEnrollCourse({
        mutation: {
            onSuccess: async (data, variables)=>{
                console.log(`[Course Enrollment] Successful for course: ${courseTitle}`, {
                    data,
                    variables
                });
                console.log('[Course Enrollment] Calling onEnrollmentSuccess callback');
                try {
                    // ✅ 낙관적 업데이트: 수강 신청 성공 즉시 로컬 상태 업데이트
                    console.log('[Course Enrollment] Setting local enrolled state to true immediately');
                    setLocalEnrolled(true);
                    setOptimisticState('enroll');
                    // ✅ CourseDetail 쿼리 무효화하여 최신 owned 상태 가져오기
                    console.log('[Course Enrollment] Invalidating course detail query to get updated owned status');
                    try {
                        const queryKey = getGetCourseDetailQueryKey(courseId);
                        console.log('[Course Enrollment] Using queryKey:', queryKey);
                        await queryClient.invalidateQueries({
                            queryKey
                        });
                        console.log('[Course Enrollment] Course detail query invalidated successfully');
                    } catch (error) {
                        console.error('[Course Enrollment] Failed to invalidate course detail query:', error);
                    }
                    if (onEnrollmentSuccess) {
                        // enrollmentId를 포함하여 성공 콜백 호출
                        await onEnrollmentSuccess(data?.data?.enrollmentId);
                    }
                    toast.success(isFree ? '무료 강좌 등록이 완료되었습니다!' : '강좌 수강 신청이 완료되었습니다.', {
                        icon: '🎉'
                    });
                } catch (error) {
                    console.error('[Course Enrollment] Error in success handler:', error);
                }
                // 플래그 해제
                setIsEnrolling(false);
            },
            onError: (error)=>{
                console.error('[Course Enrollment] Failed:', error);
                let message = '수강 신청 중 오류가 발생했습니다.';
                if (error?.response?.data?.code === 'ALREADY_ENROLLED' || error?.response?.status === 409) {
                    message = '이미 수강 신청한 강좌입니다.';
                    // 이미 수강 중인 경우 로컬 상태를 true로 설정
                    console.log('[Course Enrollment] Already enrolled detected, setting local state to true');
                    setLocalEnrolled(true);
                    setOptimisticState('enroll');
                    toast.success('이미 수강 중인 강좌입니다.');
                } else if (error?.response?.data?.message?.includes('이미 수강 중') || error?.response?.data?.message?.includes('already enrolled')) {
                    message = '이미 수강 신청한 강좌입니다.';
                    console.log('[Course Enrollment] Already enrolled detected in message, setting local state to true');
                    setLocalEnrolled(true);
                    setOptimisticState('enroll');
                    toast.success('이미 수강 중인 강좌입니다.');
                } else {
                    message = error?.response?.data?.message || error?.message || message;
                    setEnrollError(message);
                    toast.error(message);
                    setTimeout(()=>setEnrollError(null), 5000);
                    setOptimisticState('none');
                    setLocalEnrolled(false);
                }
                // 모든 경우에 대해 플래그 해제
                setIsEnrolling(false);
            },
            onSettled: ()=>{
                // onSettled에서도 플래그 해제 (fail-safe)
                setIsEnrolling(false);
            }
        }
    });
    const resolvedIsEnrolled = localEnrolled;
    const resolvedShowCancelButton = Boolean(showCancelButton || resolvedIsEnrolled && onCancelEnrollment);
    const handleEnrollClick = async ()=>{
        // ✅ 중복 클릭 방지 - 맨 처음에 체크
        if (isEnrolling || enrollMutation.isLoading) {
            console.log('[CourseHeaderActions] Enrollment already in progress, ignoring duplicate click');
            return; // 이미 처리 중이면 토스트도 띄우지 않음
        }
        if (!isAuthenticated) {
            toast.error('로그인이 필요합니다.');
            // TODO: 로그인 페이지로 리다이렉트
            return;
        }
        // ✅ 이미 수강 중인 경우 버튼 클릭을 완전히 막기
        if (isAlreadyEnrolled) {
            console.log('[CourseHeaderActions] Already enrolled, ignoring click');
            toast.info('이미 수강 중인 강좌입니다.');
            return;
        }
        if (isFree) {
            // ✅ 즉시 플래그 설정하여 중복 클릭 방지
            setIsEnrolling(true);
            setEnrollError(null);
            const studentId = user?.id ? String(user.id) : null;
            if (!studentId) {
                setIsEnrolling(false);
                const message = '사용자 정보를 불러올 수 없어 수강 신청을 진행할 수 없습니다.';
                setEnrollError(message);
                toast.error(message);
                return;
            }
            console.log('[CourseHeaderActions] Starting enrollment mutation', {
                courseId,
                studentId,
                enrollmentType: EnrollmentCreateRequestEnrollmentType.REGULAR
            });
            enrollMutation.mutate({
                courseId: courseId,
                data: {
                    courseId: courseId,
                    studentId,
                    enrollmentType: EnrollmentCreateRequestEnrollmentType.REGULAR
                }
            });
        } else {
            // 유료 강좌는 결제 페이지로 이동
            // TODO: 결제 페이지 구현
            toast('유료 강좌 결제 기능은 준비 중입니다.');
        }
    };
    const handleCancelClick = async ()=>{
        console.log('[CourseHeaderActions] Starting cancellation for course:', courseId);
        if (onCancelEnrollment) {
            try {
                const result = onCancelEnrollment();
                const resolved = result instanceof Promise ? await result : result;
                if (resolved === false) {
                    console.log('[CourseHeaderActions] Cancellation was rejected');
                    return;
                }
            } catch (error) {
                console.error('[CourseHeaderActions] Cancellation callback rejected', error);
                return;
            }
        } else {
            console.warn('[CourseHeaderActions] onCancelEnrollment callback is not provided');
            return;
        }
        console.log('[CourseHeaderActions] Cancellation successful (UI state updated)');
        setLocalEnrolled(false);
        setOptimisticState('cancel');
    };
    // 강사인 경우 - 편집 버튼 표시
    // learn.ugot.uk has no enrollment: published courses open without it.
    if (isLearnSurface()) return null;
    if (isInstructor && onEditClick) {
        return /*#__PURE__*/ _jsx(ActionsWrapper, {
            children: /*#__PURE__*/ _jsx(Button, {
                variant: "outline",
                size: "lg",
                onClick: onEditClick,
                "data-testid": "course-edit-button",
                children: "강의 편집"
            })
        });
    }
    // 이미 수강 중인 경우 - 수강 취소 버튼만 표시
    if (isAlreadyEnrolled && resolvedShowCancelButton) {
        return /*#__PURE__*/ _jsx(ActionsWrapper, {
            children: /*#__PURE__*/ _jsx(CancelButton, {
                variant: "danger",
                size: "sm",
                onClick: handleCancelClick,
                "data-testid": "course-cancel-button",
                children: "수강 취소"
            })
        });
    }
    // 수강신청 가능한 경우
    if (!isAlreadyEnrolled) {
        return /*#__PURE__*/ _jsxs(ActionsWrapper, {
            children: [
                /*#__PURE__*/ _jsxs(EnrollButton, {
                    variant: "primary",
                    size: "lg",
                    onClick: handleEnrollClick,
                    disabled: isEnrolling || enrollMutation.isLoading || isAlreadyEnrolled,
                    "data-testid": "course-enroll-button",
                    children: [
                        /*#__PURE__*/ _jsx(ShoppingCart, {
                            size: 16
                        }),
                        isEnrolling || enrollMutation.isLoading ? '처리 중...' : '수강 신청'
                    ]
                }),
                enrollError && /*#__PURE__*/ _jsx(ErrorMessage, {
                    children: enrollError
                })
            ]
        });
    }
    return null;
};
